import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useTranslation } from "react-i18next";
import { knowledgeQualityChipColor, knowledgeQualityLabel } from "./AgentKnowledgeExtractDialog";

function docIcon(name) {
  const n = (name || "").toLowerCase();
  if (n.endsWith(".pdf")) return <PictureAsPdfRoundedIcon sx={{ color: "error.main", fontSize: 28 }} />;
  if (n.endsWith(".xls") || n.endsWith(".xlsx")) return <TableChartRoundedIcon sx={{ color: "success.main", fontSize: 28 }} />;
  return <DescriptionRoundedIcon sx={{ color: "primary.main", fontSize: 28 }} />;
}

export default function AgentDocumentsSection({
  documents = [],
  fileInputRef,
  handleClickAdd,
  handleDrop,
  handleDragOver,
  moveDocument,
  removeDocument,
  readOnly = false,
  accept,
  embedded = false,
  embeddedDocTypeLabel,
  uploadTip,
  knowledgePreviews = {},
  onPreviewDocument,
}) {
  const theme = useTheme();
  const { t } = useTranslation("settings");

  const content = (
    <>
      {embedded && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {embeddedDocTypeLabel || "Documentos (PDF, Word, planilhas)"}
        </Typography>
      )}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Box
            onDrop={readOnly ? undefined : handleDrop}
            onDragOver={readOnly ? undefined : handleDragOver}
            sx={{
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              background:
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1,
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DragIndicatorRoundedIcon fontSize="small" color="action" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Arraste e solte documentos aqui</Typography>
              <Typography variant="caption" color="text.secondary">
                ou{" "}
                <Button
                  size="small"
                  variant="text"
                  onClick={readOnly ? undefined : handleClickAdd}
                  startIcon={<AttachFileRoundedIcon />}
                  disabled={readOnly}
                >
                  selecionar arquivos
                </Button>
              </Typography>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              multiple
              hidden
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const ev = new CustomEvent("agent-documents:addFiles", { detail: files });
                window.dispatchEvent(ev);
                e.target.value = "";
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {uploadTip ||
              "Dica: PDF escaneado (apenas imagem) não produz texto para o agente. Use PDFs com texto selecionável ou descreva o conteúdo no campo de instruções do agente."}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            {documents.map((doc, idx) => {
              const url = doc?.url || doc?.file_url;
              const localFile = doc?.file instanceof File ? doc.file : null;
              const canOpen = Boolean(url || localFile);
              const preview = url ? knowledgePreviews?.[`doc:${url}`] : null;
              const openLabel = t("agentsListPage.knowledgeOpenFile", {
                defaultValue: "Abrir arquivo em nova aba",
              });

              const openLocalFile = () => {
                if (!localFile) return;
                const blobUrl = URL.createObjectURL(localFile);
                window.open(blobUrl, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
              };

              const docThumbSx = {
                mt: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                maxWidth: "100%",
                textDecoration: "none",
                color: "inherit",
                border: "none",
                background: "none",
                p: 0,
                cursor: canOpen ? "pointer" : "default",
                borderRadius: 1,
                "&:hover": canOpen
                  ? {
                      opacity: 0.85,
                      "& .doc-name": { textDecoration: "underline" },
                    }
                  : undefined,
              };

              const docThumb = (
                <>
                  {docIcon(doc.name)}
                  <Typography
                    className="doc-name"
                    variant="caption"
                    sx={{
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    {doc.name}
                  </Typography>
                </>
              );

              return (
                <Grid item key={doc.id ?? idx}>
                  <Box
                    sx={{
                      width: 150,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      overflow: "hidden",
                      p: 1.5,
                      bgcolor: "background.paper",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {url ? (
                      <Tooltip title={openLabel}>
                        <Box
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={openLabel}
                          sx={docThumbSx}
                        >
                          {docThumb}
                        </Box>
                      </Tooltip>
                    ) : localFile ? (
                      <Tooltip title={openLabel}>
                        <Box
                          component="button"
                          type="button"
                          onClick={openLocalFile}
                          aria-label={openLabel}
                          sx={docThumbSx}
                        >
                          {docThumb}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box sx={docThumbSx}>{docThumb}</Box>
                    )}
                    {preview && (
                      <Chip
                        size="small"
                        color={knowledgeQualityChipColor(preview.quality)}
                        label={knowledgeQualityLabel(t, preview.quality)}
                        sx={{ maxWidth: "100%" }}
                      />
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <Chip size="small" label={`#${idx + 1}`} />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => moveDocument(idx, -1)}
                          disabled={readOnly || idx === 0}
                        >
                          <ArrowBackIosNewRoundedIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveDocument(idx, 1)}
                          disabled={readOnly || idx === documents.length - 1}
                        >
                          <ArrowForwardIosRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignSelf: "stretch",
                        justifyContent: "space-between",
                        mt: 0.25,
                      }}
                    >
                      {typeof onPreviewDocument === "function" ? (
                        <Tooltip title={t("agentsListPage.knowledgePreviewAction")}>
                          <IconButton
                            size="small"
                            onClick={() => onPreviewDocument(doc, idx)}
                            aria-label="preview-document"
                          >
                            <VisibilityOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <span />
                      )}
                      {!readOnly && (
                        <IconButton size="small" onClick={() => removeDocument(idx)} aria-label="Remover">
                          <DeleteOutlineRoundedIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </>
  );

  if (embedded) {
    return <Box sx={{ mb: 0 }}>{content}</Box>;
  }
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title="Documentos de referência"
        subheader="Word, planilhas e PDF. Arraste e solte ou selecione os arquivos."
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 1 }}>{content}</CardContent>
    </Card>
  );
}
